
import img0 from './Images/AddPackageSource.png';
import img1 from './Images/SearchDependencyPackage.png';
import img2 from './Images/AddSqlClientAssembly.png';
import img3 from './Images/GenerateUserToken.png';
import img4 from './Images/AddSqlClientAssembly.png';
  
const content = `# OnData SqlClient Database Driver

The OnData database drivers can be used to enforce the data access policies for the confidential and regulated data. The data access roles, just like the application user roles, can 
be assigned to a user when the user signs into applications through an identity and access management (IAM) system. Such data access roles are embedded in the user's token. When such 
user tokens are passed to an OnData database driver, the driver will validate such tokens, identify the data access roles explicitly granted to a user, and enforce the data access 
permissions so that the user can only retrieve or store data that the user has access to. 

The OnData SqlClient Data Provider for SQL Server (**OnData.SqlClient**) is ADO.NET provider that supports the .NET Framework and the .NET Core Framework. It can be used in place 
of the **Microsoft.Data.SqlClient** to enable the dynamic encryption and decryption of the confidential and regulated data. Minimal code changes are required to pass the user tokens 
to the OnData database driver.

OnData SqlClient features:

- Data Encryption: For table columns with encryption rules configured at the OnData Portal, data will be encrypted before being stored to the SQL server database. The strong 
encryption significantly enhances the protection of the confidential and regulated data against both external and internal threats.
- Data Decryption: If a user does have access to the encrypted data, such data will be seamlessly decrypted for the user. Otherwise, the data will stay as encrypted or masked 
per the masking rules configured at the OnData Portal.

The **OnData.EntityFrameworkCore.SqlServer** is a database provider based on the **OnData.SqlClient**. It allows the Entity Framework Core to be used with Microsoft SQL Server 
(including Microsoft SQL Azure) while enforcing the data access policies leveraging the OnData database drivers. 

## 1. How to Use OnData.SqlClient

### 1.1 Install **OnData.SqlClient.Dependency** from NuGet

As the first step, you need to install the OnData.SqlClient.Dependency package from NuGet. If you cannot find this package from your package source in Visual Studio, please 
add the \`https://api.nuget.org/v3/index.json\` to the package source, as illustrated below.

![Add package source](${ img0 })

![Search package](${ img1 })

### 1.2 Add Reference to OnData.SqlClient

Download the **OnData.SqlClient.dll** and in your project add reference to that assembly, as illustrated below.

![Add Assembly](${ img2 })

**Note**: If your project has a reference to the **Microsoft.Data.SqlClient** assembly, please remove it and the use the reference to the **OnData.SqlClient** assembly instead.

### 1.3 Add OnData.SqlClient Dependencies

Inside the OnData driver package, you will find the list of OnData.SqlClient dependency files as illustrated below. Such files are required for running your applications with 
the OnData.SqlClient.dll. There is no need to add reference to them in your project, but you need to have them in the same directory as your compiled applications.  
  
**Windows**:
- EncryptSQL.dll: OnData driver library for Windows
- libcrypto-1_1-x64.dll: From the OpenSSL library
- libpq.dll: From the OpenSSL library
- libssl-1_1-x64.dll: From the OpenSSL library
- OnDataDriver.config: OnData driver configuration file
- Configurator.exe: OnData driver configuration utility
- gdroot-g2.crt: The root certificate from the GoDaddy Class 2 Certification Authority

**Linux**:
- EncryptSQL.so: OnData driver library for Linux
- OnDataDriver.config: OnData driver configuration file
- Configurator: OnData driver configuration utility
- gdroot-g2.crt: The root certificate from the GoDaddy Class 2 Certification Authority

As an example, you may use the following configuration to always copy the dependency files to the output directory for a Windows configuration.
\`\`\`c#
  <ItemGroup>
    <None Update="EncryptSQL.dll">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="libcrypto-1_1-x64.dll">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="libpq.dll">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="libssl-1_1-x64.dll">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="OnDataDriver.config">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="Configurator.exe">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
    <None Update="gdroot-g2.crt">
      <CopyToOutputDirectory>Always</CopyToOutputDirectory>
    </None>
  </ItemGroup>
\`\`\`

### 1.4 Register as OnData Client
Before the OnData SqlClient can be used, it must be registered and activated as an OnData client. You can either run the OnData Configurator tool included in the OnData SqlClient 
release package to register it as a driver client, or create a driver client directly at the OnData Portal and populate the corresponding ClientId and ClientSecret information 
from the OnData Portal to the OnDataDriver.config file. For details on how to register a driver client with the OnData Platform, please refer to 
the [**OnData Client Registration**](reference_doc#securedb/database_drivers/ondata_client_registration).  

### 1.5 Set User Token

In order to manage the data access for each user signing into your application, the application must be able to generate or get user tokens upon user sign in. A user token is a 
JWT (JSON Web Token) which contains a list of data access roles. The data access roles are configured at the OnData Portal and can represent the read and write permissions 
for different data classifications. In your application, you can set a user token to a **SqlConnection** and then all commands under this SqlConnection instance will use that 
user token to access data through the OnData driver. It is recommended to create a new instance of SqlConnection per request or task.

Note: If you can only get user information in a controller, page or endpoint and the SqlConnection is injected in service or repository instance, you can add a method in the service 
or repository to set user tokens. If you are using dependency injection, it is recommended to inject such services or repositories with transient or scoped lifetime.

![Set user token](${ img3 })

A user token can be either generated by your application itself or obtained from a commercial off-the-shelf Identity and Access Management (IAM) system, such as Azure AD and Okta.

#### 1.5.1 Generate User Token in Application
If you choose to generate the user tokens through your application, please follow the steps below:

- Generate a RSA key pair, upload the public key to the OnData Portal (under Settings->Encryption->Keys) as a token validation key. OnData driver will use the token validation keys 
to validate the user tokens.
- Figure out the data access roles of the login user. You can get the user info from HttpContext or Session, then determine the data access roles assigned to that login user. 
If a SqlConnection instance is used to process some background tasks that are not associated with any specific user, then determine data access roles needed for that background process.
- Generate a JWT with the RSA private key, and add an attribute called dataAccessRoles, which is an array of data access roles, as a claim to the payload. It is recommended that the 
username is also added to the JWT token in case you want to log data access history.
- The OnData.SqlClient provides an extension method to pass the user tokens to the SqlConnection instance.  
  
Below is the sample code for generating a user token for a given username and associated data access roles.  

\`\`\`c#
// Generate a user token according to the data access roles, which is a list of strings
string userToken = GenerateUserToken(dataAccessRoles, username);

using (var conn = new SqlConnection(connectionStr))
{
  conn.SetUserToken(userToken);
  conn.Open();
  var cmd = conn.CreateCommand();
  command.CommandText = "SELECT COUNT(*) FROM Categories;";
  cmd.ExecuteScalar()
}

private string GenerateUserToken(List<string> dataAccessRoles, string username)
{
  // Import private key from the RSA key pair
  var privateKey = _settings.RsaPrivateKey.ToByteArray();
  using var rsa = RSA.Create();
  rsa.ImportRSAPrivateKey(privateKey, out _);

  var signingCredentials = new SigningCredentials(new RsaSecurityKey(rsa), SecurityAlgorithms.RsaSha256)
  {
    CryptoProviderFactory = new CryptoProviderFactory { CacheSignatureProviders = false }
  };

  var now = DateTime.Now;
  var unixTimeSeconds = new DateTimeOffset(now).ToUnixTimeSeconds();

  var claims = new List<Claim>()
  {
    new Claim(JwtRegisteredClaimNames.Iat, unixTimeSeconds.ToString(), ClaimValueTypes.Integer64),
    new Claim(JwtRegisteredClaimNames.Jti, Guid.NewGuid().ToString()),
    new Claim(JwtRegisteredClaimNames.Sub, username)
  };
  foreach (var role in dataAccessRoles)
  {
    claims.Add(new Claim("dataAccessRoles", role));
  }
  var jwt = new JwtSecurityToken(
    audience: _settings.Audience,
    issuer: _settings.Issuer,
    claims: claims,
    notBefore: now,
    expires: now.AddMinutes(30),
    signingCredentials: signingCredentials
  );
  return new JwtSecurityTokenHandler().WriteToken(jwt);
}
\`\`\`

#### 1.5.2 Get User Token from IAM System
If your application is integrated with an Identity and Access Management (IAM) system for user authentication and authorization management, please follow the steps below:
- Add a custom claim named dataAccessRoles into access_token.
- Upload the public key(s) of the IAM System to the OnData Portal (under Settings->Encryption->Keys) as token validation key(s).
- In your code, get the access_token from the HttpContext object.
- The OnData.SqlClient provides an extension method to pass the user tokens to the SqlConnection instance. 
  
Below is the sample code to get the access_token from the HttpContext object and pass it to the SqlConnection object.  

\`\`\`c#
string userToken = await HttpContext.GetTokenAsync("access_token");
using (var conn = new SqlConnection(connectionStr))
{
  conn.SetUserToken(userToken);
  conn.Open();
  var cmd = conn.CreateCommand();
  command.CommandText = "SELECT COUNT(*) FROM Categories;";
  cmd.ExecuteScalar()
}
\`\`\`

### 1.6 Trust Server Certificate Connection String

The OnData driver requires the connection string to include this attribute: \`TrustServerCertificate=True;\`

## 2. How to Use OnData.EntityFrameworkCore.SqlServer

### 2.1 Install **OnData.SqlClient.Dependency** from NuGet

Please refer to Section 1.1 above on how to install the OnData.SqlClient.Dependency from NuGet.

### 2.2 Add Reference to OnData.SqlClient.dll

Please refer to Section 1.2 above on how to add reference to OnData.SqlClient.dll.

### 2.3 Add OnData.SqlClient Dependencies

Please refer to Section 1.3 above on how to add the OnData.SqlClient dependencies.

### 2.4 Add Reference to OnData.EntityFrameworkCore.SqlServer.dll

Download **OnData.EntityFrameworkCore.SqlServer.dll** and in your project add reference to that assembly, as illustrated below.

![Add Assembly](${ img2 })

**Note**: If your project has a reference to the **Microsoft.EntityFrameworkCore.SqlServer** assembly, please remove it and the use the reference to 
the **OnData.EntityFrameworkCore.SqlServer** assembly instead.

### 2.5 Register as OnData Client

Please refer to Section 1.4 above on how to register the OnData driver as an OnData client.

### 2.6 Set User Token

In order to manage the data access for each user signing into your application, the application must be able to generate or get user tokens upon user sign in. A user token is a 
JWT (JSON Web Token) which contains a list of data access roles. The data access roles are configured at the OnData Portal and can represent the read and write permissions 
for different data classifications. In your application, you can set a user token to a **DbContext** and then all commands under this DbContext instance will use that user token 
to access data through OnData driver. It is recommended to create a new instance of DbContext per request or task.

For more details on how to generate a user token or get a user token from an IAM system, please refer to Section 1.5 above. The code below illustrates how to set a user token to 
a DbContext object.

\`\`\`c#
// Set a user token to the DbContext object
using (var dbContext = new MyDbContext())
{
  dbContext.SetUserToken(userToken);
  var category = dbContext.Categories.Find(categoryId);
  category.Description = "New Description";
  dbContext.SaveChanges();
}
\`\`\`

Note: If you can only get user information in a controller, page or endpoint but the DbContext is injected in service or repository instance, you can add a method in the service 
or repository for setting the user tokens.

### 2.7 Trust Server Certificate Connection String

The OnData driver requires the connection string to include this attribute: \`TrustServerCertificate=True;\`

## 3. Possible Exceptions

If something goes wrong, one of the following error codes could be returned. 

\`\`\`c#
// Error codes:
NULL_ENCRYPT_CONTROLLER = -1,
FAILED_CONNECT_ONDATA = -2,
FAILED_PARSE_STATEMENT = -3,
FAILED_EXECUTION_PERMISSION = -4,
STATEMENT_NOT_FOUND = -5,
PARAMETER_NOT_FOUND = -6,
COLUMN_NOT_FOUND = -7,
FAILED_PARAMETER_ENCRYPTION = -8,
FAILED_VALUE_DECRYPTION = -9,
FAILED_INSUFFICIENT_BUFFER = -10
\`\`\`
  `
  
export default content;
